/**
 * Preseed "select all matching" gating — the handler-level refusal.
 *
 * The preseed tab has no bulk endpoint wiring: the backend bulk entity
 * allowlist excludes `preseed`, so a preseed approve/reject fans out one PUT
 * per *loaded* row. When the Gmail-style banner promotes the selection to
 * `'all'` mode ("Select all N matching"), the handlers still only see the
 * loaded page — so they mutate a page and report success for all N. ENG-5389.
 *
 * This module is the handler-level half of that refusal. Its only production
 * consumer is `hooks/actions/useSeedsActionHandlers.ts`, which calls it at the
 * top of the preseed branch of `handleApprove` and `handleReject`.
 *
 * The UI-level gate is a *separate* predicate and deliberately NOT a consumer
 * of this module: `sections/seeds/index.tsx` computes
 * `enableSelectAllMatching = activeQuery != null && type !== 'preseed'`, which
 * tests entity *identity* (which tab is mounted), whereas this module tests
 * selection *mode* (`mode === 'all'`). Different questions over different
 * inputs — there is no single source to share between them.
 *
 * Because the UI gate removes the only route into `'all'` mode on the preseed
 * tab, this refusal is unreachable through the app today; it is kept as defense
 * in depth against that gate being re-enabled for preseeds. It does NOT cover
 * the case where the enclosing `isPreseed(selectedSeeds[0])` branch itself
 * stops matching — see the comment on `isPreseed` in
 * `hooks/actions/useSeedsActionHandlers.ts`.
 *
 * Deliberately pure: no React import, type-level import only, so it is
 * trivially unit-testable.
 */

import type { BulkSelectionState } from '@/hooks/useBulkSelection/types';

/** Copy shown when a preseed mutation is refused in "select all matching" mode. */
export const PRESEED_SELECT_ALL_REFUSAL =
  'Select all matching is not available for preseeds — select specific rows instead.';

/**
 * Returns the refusal copy when the selection has been promoted to
 * `'all'` mode, or `null` when the mutation may proceed.
 *
 * Fails open on `undefined` — `selectionState` is optional on
 * `SeedsActionHandlerConfig`, and a caller that never wires bulk selection has
 * no way to reach `'all'` mode in the first place.
 */
export function getPreseedSelectAllRefusal(
  selectionState?: Pick<BulkSelectionState, 'mode'>
): string | null {
  return selectionState?.mode === 'all' ? PRESEED_SELECT_ALL_REFUSAL : null;
}
